/**
 * Shared reconciliation source for LOYALTY REDEMPTIONS.
 *
 * Both the Loyalty Report (Points Redeemed) and the Sales Report's
 * discount-source breakdown ("Loyalty Redemption") derive their numbers from
 * this one module, so the two reports tie out for the same period + outlet
 * scope (SALES-LOY-1 reconciliation criterion).
 *
 * The per-day redeemed formula MUST stay identical to LoyaltyReport's `genDay`
 * (the first two rng draws: issued, then redeemed). Keep them in sync.
 */

/** Uniform Dollar Value per Point from the loyalty program's redemption settings. */
export const LOY_DOLLAR_PER_POINT = 0.05;

/** Program-wide outlet factors (shared program). `null` / unknown = all outlets. */
const OUTLET_FACTORS: Record<string, number> = { all: 1, downtown: 0.45, airport: 0.33, riverside: 0.22 };
export const outletFactorFor = (id: string | null | undefined): number => OUTLET_FACTORS[id ?? "all"] ?? 1;

const hashStr = (s: string): number => {
    let h = 2166136261;
    for (let i = 0; i < s.length; i++) {
        h ^= s.charCodeAt(i);
        h = Math.imul(h, 16777619);
    }
    return h >>> 0;
};

const mulberry32 = (seed: number) => () => {
    seed |= 0; seed = (seed + 0x6D2B79F5) | 0;
    let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
};

/** Deterministic RNG seeded by a string key — shared by the discount-log generator. */
export const makeRng = (key: string) => mulberry32(hashStr(key));

// Redeemed points for a single calendar day — identical to LoyaltyReport genDay.
function dayRedeemedPoints(iso: string, outletId: string | null): number {
    const f = outletFactorFor(outletId);
    const rng = mulberry32(hashStr(iso + "|" + (outletId ?? "all")));
    const dow = new Date(iso + "T12:00:00").getDay();
    const weekend = dow === 5 || dow === 6 ? 1.35 : 1;
    const issued = Math.round((3200 + rng() * 3400) * weekend * f); // 1st rng draw
    return Math.round(issued * (0.30 + rng() * 0.22));              // 2nd rng draw
}

const DAY_MS = 86400000;
const parseLocalMidnight = (iso: string): Date | null => {
    const [y, m, d] = (iso || "").split("-").map(Number);
    if (!y || !m || !d) return null;
    return new Date(y, m - 1, d); // local midnight — matches LoyaltyReport _startOfDay
};
const isoOf = (d: Date): string => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
};

/** Total loyalty points redeemed across [startISO, endISO] inclusive, scoped by outlet. */
export function getLoyaltyRedemptionPoints(startISO: string, endISO: string, outletId: string | null = null): number {
    const s = parseLocalMidnight(startISO);
    const e = parseLocalMidnight(endISO);
    if (!s || !e || e.getTime() < s.getTime()) return 0;
    let total = 0;
    for (let t = s.getTime(); t <= e.getTime(); t += DAY_MS) total += dayRedeemedPoints(isoOf(new Date(t)), outletId);
    return total;
}

/** Dollar value of loyalty points redeemed in the period — the discount those points delivered. */
export const getLoyaltyRedemptionDollar = (startISO: string, endISO: string, outletId: string | null = null): number =>
    getLoyaltyRedemptionPoints(startISO, endISO, outletId) * LOY_DOLLAR_PER_POINT;

export interface DiscountSourceBreakdown {
    promotional: number;   // Offers
    loyalty: number;       // Loyalty Redemption (reconciles with Loyalty Report)
    manualComp: number;    // Manual & Comp
    total: number;
}

/**
 * Discount total split by source for the Sales Report's Discount & Promotion
 * Performance table. Loyalty Redemption is the reconciliation figure above;
 * Promotional and Manual & Comp are synthesized deterministically from the
 * period so the three are internally consistent (frontend, no source-tag API
 * yet — see SALES-LOY-1 technical notes).
 */
export function getDiscountSourceBreakdown(startISO: string, endISO: string, outletId: string | null = null): DiscountSourceBreakdown {
    const round2 = (n: number) => Math.round(n * 100) / 100;
    const loyalty = round2(getLoyaltyRedemptionDollar(startISO, endISO, outletId));
    const rng = mulberry32(hashStr("disc|" + startISO + "|" + endISO + "|" + (outletId ?? "all")));
    const promotional = round2(loyalty * (1.8 + rng() * 0.9)); // offers ≈ 1.8–2.7× loyalty
    const manualComp = round2(loyalty * (0.2 + rng() * 0.25)); // comps ≈ 0.2–0.45× loyalty
    return { promotional, loyalty, manualComp, total: round2(promotional + loyalty + manualComp) };
}

export const DISCOUNT_SOURCES = [
    { key: "promotional" as const, label: "Promotional (Offers)", mobileLabel: "Promotional" },
    { key: "loyalty" as const, label: "Loyalty Redemption", mobileLabel: "Loyalty" },
    { key: "manualComp" as const, label: "Manual", mobileLabel: "Manual" },
];
export type DiscountSourceKey = (typeof DISCOUNT_SOURCES)[number]["key"];
